import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  Page,
  PageTitleWithButton,
  Table,
  Pagination,
  Loader,
  MobileTable,
  SearchSection
} from '../../../components';
import { useQuery } from 'react-query';
import { Box, Button, useMediaQuery } from '@mui/material';
import { Edit, Add, DeleteForever } from '@mui/icons-material';
import { formatInputPhoneNumber } from '../../../helpers';
import { getSupervisors, deleteSupervisor } from '../../../fetch';
import { IResponse, ISupervisor } from '../../../models';
import { SupervisorModal } from './supervisors-modal';
import { SupervisorsFilters } from './page-filters';

export const SupervisorsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingSupervisor, setLoadingSupervisor] = useState<boolean>(false);
  const [isModalOpen, showSupervisorModal] = useState<boolean>(false);
  const [currentSupervisorId, setCurrentSupervisorId] = useState<string | number>(
    undefined
  );
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    DisplayName?: 'Asc' | 'Desc';
    ContactPhoneNumber?: 'Asc' | 'Desc';
    ActiveBuildCount?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search?: string;
    neighborhoodId?: string | number;
    territoryId?: string | number;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const isMobile = useMediaQuery('(max-width: 960px)');

  const {
    isLoading: isLoadingSupervisors,
    data: supervisorsData,
    refetch: refetchSupervisors
  } = useQuery<IResponse<ISupervisor[]>, Error>(
    ['getSupervisors', page, perPage, selectedSort, sortDirection, filters, searchedValue],
    () =>
      getSupervisors({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        searchTerm: searchedValue || filters?.search,
        territoryId: filters?.territoryId,
        neighborhoodId: filters?.neighborhoodId
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const supervisors = useMemo(() => supervisorsData?.records ?? [], [supervisorsData]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleEdit = async (id: string | number) => {
    setCurrentSupervisorId(id);
    showSupervisorModal(true);
  };
  const handleDelete = async (id: string | number) => {
    const result = window.confirm('Are you sure you want to delete this supervisor?');

    if (result) {
      setLoadingSupervisor(true);
      try {
        await deleteSupervisor(id);
        refetchSupervisors();
        enqueueSnackbar('Supervisor removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error deleting supervisor, please try again.`, {
          variant: 'error'
        });
      } finally {
        setLoadingSupervisor(false);
      }
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Display Name',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'DisplayName',
        isServerSortedDesc: sortDirection.DisplayName === 'Desc',
        handleClickColumn: () => handleClickColumn('DisplayName')
      },
      {
        Header: 'Territories',
        accessor: 'territories',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISupervisor } };
        }) => {
          return <>{original.territories.join(', ')}</>;
        }
      },
      {
        Header: 'Neighborhoods',
        accessor: 'neighborhoods',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISupervisor } };
        }) => {
          return <>{original.neighborhoods.join(', ')}</>;
        }
      },
      {
        Header: 'Contact Number',
        accessor: 'contactPhoneNumber',
        isServerSorted: selectedSort === 'ContactPhoneNumber',
        isServerSortedDesc: sortDirection.ContactPhoneNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('ContactPhoneNumber'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISupervisor } };
        }) => {
          return <>{formatInputPhoneNumber(original.contactPhoneNumber)}</>;
        }
      },
      {
        Header: 'Active Build Count',
        accessor: 'activeBuildCount',
        isServerSorted: selectedSort === 'ActiveBuildCount',
        isServerSortedDesc: sortDirection.ActiveBuildCount === 'Desc',
        handleClickColumn: () => handleClickColumn('ActiveBuildCount')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISupervisor } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEdit(original.supervisorId);
                }}
              >
                Edit
              </Button>
              <Button
                id="delete"
                color="error"
                startIcon={<DeleteForever />}
                onClick={async () => {
                  handleDelete(original.supervisorId);
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisors]);
  return (
    <>
      {isLoadingSupervisor && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Supervisors">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Supervisors',
            showDivider: false
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => showSupervisorModal(true)}
              color="primary"
              variant="contained"
            >
              Add New Supervisor
            </Button>
          }
        />
        <Box mt={1}>
          <SupervisorsFilters
            isLoading={isLoadingSupervisors}
            setHasAppliedFilters={setHasAppliedFilters}
            hasAppliedFilters={hasAppliedFilters}
            setSearchValue={setSearchValue}
            setSearchedValue={setSearchedValue}
            searchValue={searchValue}
            setSelectedNeighborhoodId={setSelectedNeighborhoodId}
            selectedNeighborhoodId={selectedNeighborhoodId}
            setSelectedTerritoryId={setSelectedTerritoryId}
            selectedTerritoryId={selectedTerritoryId}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchedValue(val);
            }}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setFilters({
                  search: '',
                  neighborhoodId: undefined,
                  territoryId: undefined
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  search: searchValue,
                  neighborhoodId: selectedNeighborhoodId || undefined,
                  territoryId: selectedTerritoryId || undefined
                });
              }
            }}
          />
          {searchedValue && (
            <SearchSection
              searchedValue={searchedValue}
              handleClearSearch={() => {
                setSearchValue('');
                setSearchedValue('');
                setHasAppliedFilters(false);
                setPage(0);
              }}
            />
          )}
        </Box>
        <Table
          columns={columns}
          data={supervisors}
          isLoading={isLoadingSupervisors}
          hidePagination
          mobileProps={{
            handleEdit: (val: ISupervisor) => {
              handleEdit(val.supervisorId);
            },
            handleDelete: (val: ISupervisor) => {
              handleDelete(val.supervisorId);
            },
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
        {!isLoadingSupervisors && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <SupervisorModal
          supervisorId={currentSupervisorId}
          open={isModalOpen}
          onClose={() => {
            showSupervisorModal(false);
            setTimeout(() => {
              setCurrentSupervisorId(undefined);
            }, 500);
          }}
          fetchSupervisors={refetchSupervisors}
        />
      </Page>
    </>
  );
};
